import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import styles from 'src/components/fields/EditableField/EditableField.module.css';
import cx from 'classnames';
import { Typography } from 'antd';
import { useClickAway } from 'react-use';

export interface EditableFieldProps {
  children: ReactNode;
  value: string | number | boolean;
  className?: string;
  onEditModeChange?: (e: boolean) => void;
  disableClickAway?: boolean;
  onEscape?: () => void;
  level?: 2 | 1 | 5 | 3 | 4 | undefined;
}

const EditableField: React.FC<EditableFieldProps> = props => {
  const { value, children, onEditModeChange, className, disableClickAway, level, onEscape } = props;

  const ref = useRef<HTMLDivElement>(null);

  const [editMode, setEditMode] = useState<boolean>(false);

  const emitEditModeCallback = useCallback(
    (e: boolean) => {
      if (onEditModeChange) {
        onEditModeChange(e);
      }
    },
    [onEditModeChange]
  );

  const handleValueClick = useCallback(() => {
    setEditMode(true);
  }, []);

  useEffect(() => {
    setEditMode(false);
  }, [emitEditModeCallback, value]);

  useClickAway(ref, () =>
    setTimeout(() => {
      if (!disableClickAway) {
        setEditMode(false);
      }
    }, 400)
  );

  useEffect(() => {
    emitEditModeCallback(editMode);
  }, [editMode, emitEditModeCallback]);

  const titleRender = useCallback(
    (value: string | number | boolean) =>
      level ? (
        <Typography.Title level={level} style={{ whiteSpace: 'pre-wrap', margin: 0 }} className={className}>
          {value}
        </Typography.Title>
      ) : (
        <Typography.Text style={{ whiteSpace: 'pre-wrap' }} className={className}>
          {value}
        </Typography.Text>
      ),

    [className, level]
  );

  return (
    <div
      ref={ref}
      className={cx(styles.wrap, { [styles.editing]: editMode })}
      onClick={handleValueClick}
      onKeyDown={event => {
        if (event.code === 'Escape') {
          setEditMode(false);
          onEscape?.();
          event.preventDefault();
        }
      }}
    >
      {!editMode && titleRender(value)}
      {editMode && children}
    </div>
  );
};

export default EditableField;
